import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ViewEncapsulation
} from '@angular/core';
import {
  FeatureBodyDirective,
  FeatureFooterDirective,
  FeatureIconDirective,
  FeatureIntroDirective,
  FeatureTitleDirective
} from '../../directives';
import { IFeatureView } from '../../model';

/**
 * @description
 * a container skeleton template for any feature's main (page-level) view. in hosts 5 different (optional)
 * templates:
 * 1.iconTemplate (feature view's top icon)
 * 2.titleTemplate (feature view's main title)
 * 3.introTemplate (feature view's sub title)
 * 4.bodyTemplate (feature view's body)
 * 5.footerTemplate (feature view's footer)
 *
 * all templates are optional, meaning the container will only render template instances supplied by the
 * consumer and ignore all others template-containers, if a consumer don't need to display "top icon" - it
 * should not provide a template for it
 */

@Component({
  standalone: true,
  selector: 'feature-view',
  template: `
    <ng-container *ngIf="iconTemplate">
      <div class="feature-icon">
        <ng-container *ngTemplateOutlet="iconTemplate.tpl"></ng-container>
      </div>
    </ng-container>
    <ng-container *ngIf="titleTemplate">
      <div class="feature-title">
        <h1><ng-container *ngTemplateOutlet="titleTemplate.tpl"></ng-container></h1>
      </div>
    </ng-container>

    <ng-container *ngIf="introTemplate">
      <div class="feature-intro">
        <h3><ng-container *ngTemplateOutlet="introTemplate.tpl"></ng-container></h3>
      </div>
    </ng-container>

    <ng-container *ngIf="bodyTemplate">
      <div class="feature-body">
        <ng-container *ngTemplateOutlet="bodyTemplate.tpl"></ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="footerTemplate">
      <div class="feature-footer">
        <ng-container *ngTemplateOutlet="footerTemplate.tpl"></ng-container>
      </div>
    </ng-container>
  `,
  imports: [NgIf, NgTemplateOutlet],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'feature-view'
  }
})
export abstract class FeatureViewComponent implements IFeatureView, AfterViewInit {
  @ContentChild(FeatureIconDirective, { static: true })
  iconTemplate: FeatureIconDirective;

  @ContentChild(FeatureTitleDirective, { static: true })
  titleTemplate: FeatureTitleDirective;

  @ContentChild(FeatureIntroDirective, { static: true })
  introTemplate: FeatureIntroDirective;

  @ContentChild(FeatureBodyDirective, { static: true })
  bodyTemplate: FeatureBodyDirective;

  @ContentChild(FeatureFooterDirective, { static: true })
  footerTemplate: FeatureFooterDirective;

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.changeDetector.detectChanges();
  }
}
